import * as d3 from 'd3-hierarchy'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IValueType } from '../MenuItem'

const getDisplayNames = (data: d3.HierarchyNode<IValueType> | null): string[] =>
  !data?.data.displayName || data.id === 'root'
    ? []
    : [...getDisplayNames(data.parent), data.data.displayName]

const useSearch = (
  searching: boolean,
  options: d3.HierarchyNode<IValueType>,
) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [value, onChange] = useState('')
  const leaves = useMemo(
    () =>
      options.leaves().map((l) => ({
        ...l,
        data: {
          ...l.data,
          displayName: getDisplayNames(l).join(' / '),
        },
      })),
    [options],
  )
  const searchOptions = useMemo(() => {
    if (!value) return options.children

    const filteredLeaves = leaves.filter((l) =>
      l.data.displayName?.toUpperCase().includes(value.toUpperCase()),
    )

    if (filteredLeaves.length !== 0) return filteredLeaves

    return d3.stratify<IValueType>()([
      { id: 'root' } as IValueType,
      {
        id: 'not found',
        parentId: 'root',
        displayName: 'Not found',
        disabled: true,
      },
    ]).children
  }, [leaves, value])

  useEffect(() => {
    if (searching) return

    onChange('')
    inputRef.current?.querySelector('input')?.blur()
  }, [searching])

  return {
    inputRef,
    value,
    onChange: useCallback(
      (e) => {
        if (!searching) return

        onChange(e.target.value)
      },
      [searching, onChange],
    ),
    searching,
    searchOptions: searching ? searchOptions : options.children,
  }
}

export default useSearch
